import { useEffect, useState } from 'preact/hooks';
import { api } from '../api/client';
import type { AnkiPingResponse, AnkiStatus, AnkiSyncResult } from '../api/types';
import { t } from '../i18n';
import { describeError } from '../lib/errors';
import { formatDateTime } from '../lib/format';
import { useLang } from '../lib/hooks';
import { Badge, ErrorBox, Notice, Section, Spinner } from '../components/ui';

export function AnkiPage() {
  useLang();

  const [status, setStatus] = useState<AnkiStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [ping, setPing] = useState<AnkiPingResponse | null>(null);
  const [pingError, setPingError] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState<AnkiSyncResult | null>(null);
  const [syncResult, setSyncResult] = useState<AnkiSyncResult | null>(null);
  const [busy, setBusy] = useState<'status' | 'ping' | 'dry' | 'sync' | null>('status');
  const [actionError, setActionError] = useState<string | null>(null);
  const [dryRunSupported, setDryRunSupported] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .ankiStatus()
      .then((value) => {
        if (!cancelled) setStatus(value);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setStatusError(describeError(caught));
      })
      .finally(() => {
        if (!cancelled) setBusy(null);
      });
    void api.supportsAnkiDryRun().then((supported) => {
      if (!cancelled) setDryRunSupported(supported);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const runPing = async () => {
    setBusy('ping');
    setPingError(null);
    try {
      setPing(await api.ankiPing());
    } catch (caught) {
      // A 503 from /anki/ping is a legitimate answer ("not reachable"); the
      // backend contract keeps it structured, so surface it as status rather
      // than as a page-level failure when possible.
      setPing(null);
      setPingError(describeError(caught));
    } finally {
      setBusy(null);
    }
  };

  const runDry = async () => {
    if (!dryRunSupported) return;
    setBusy('dry');
    setActionError(null);
    try {
      setDryRun(await api.ankiSync({ dryRun: true }));
    } catch (caught) {
      setActionError(describeError(caught));
    } finally {
      setBusy(null);
    }
  };

  const runSync = async () => {
    if (!window.confirm(t('anki.confirmSync'))) return;
    setBusy('sync');
    setActionError(null);
    try {
      setSyncResult(await api.ankiSync({ dryRun: false }));
    } catch (caught) {
      setActionError(describeError(caught));
    } finally {
      setBusy(null);
    }
  };

  const reachable = ping?.reachable ?? status?.reachable ?? null;
  const reason = ping?.error ?? status?.error ?? null;

  return (
    <div class="page">
      <header class="page__header">
        <div>
          <h1>{t('anki.title')}</h1>
          <p class="muted">{t('anki.subtitle')}</p>
        </div>
        <div class="page__actions">
          <button type="button" class="btn" disabled={busy !== null} onClick={() => void runPing()}>
            {busy === 'ping' ? t('anki.pinging') : t('anki.ping')}
          </button>
        </div>
      </header>

      <Notice tone="warn">{t('anki.notice')}</Notice>
      {actionError ? <ErrorBox message={actionError} /> : null}

      <Section title={t('anki.status')}>
        {busy === 'status' ? <Spinner /> : null}
        {statusError !== null ? <ErrorBox message={statusError} /> : null}
        {pingError !== null ? <ErrorBox message={pingError} /> : null}

        {reachable !== null ? (
          <p class="anki-badge">
            {reachable ? (
              <Badge tone="ok">{t('anki.reachable')}</Badge>
            ) : (
              <Badge tone="bad">{t('anki.unreachable')}</Badge>
            )}
            {!reachable && reason ? <span class="muted"> {t('anki.reason', { reason })}</span> : null}
          </p>
        ) : null}

        <dl class="kv">
          <dt>{t('anki.url')}</dt>
          <dd>
            <code>{ping?.url || status?.url || '—'}</code>
          </dd>
          <dt>{t('anki.version')}</dt>
          <dd>{ping?.version ?? status?.version ?? '—'}</dd>
          <dt>{t('anki.linked')}</dt>
          <dd>{status?.linked_notes ?? '—'}</dd>
          <dt>{t('anki.lastSync')}</dt>
          <dd>{status?.last_sync_at ? formatDateTime(status.last_sync_at) : t('anki.never')}</dd>
          <dt>{t('settings.deck')}</dt>
          <dd>{status?.deck ?? '—'}</dd>
          <dt>{t('settings.model')}</dt>
          <dd>{status?.model ?? '—'}</dd>
        </dl>
      </Section>

      <Section title={t('anki.dryRun')} description={t('anki.dryRunHint')}>
        <div class="page__actions">
          <button
            type="button"
            class="btn"
            disabled={busy !== null || !dryRunSupported}
            title={dryRunSupported ? undefined : t('anki.dryRunUnsupported')}
            onClick={() => void runDry()}
          >
            {busy === 'dry' ? t('common.loading') : t('anki.dryRun')}
          </button>
          <button type="button" class="btn btn--primary" disabled={busy !== null} onClick={() => void runSync()}>
            {busy === 'sync' ? t('anki.syncing') : t('anki.sync')}
          </button>
        </div>
        {!dryRunSupported ? <p class="muted small">{t('anki.dryRunUnsupported')}</p> : null}
        {dryRun ? <SyncSummary result={dryRun} /> : null}
        {syncResult ? <SyncSummary result={syncResult} /> : null}
      </Section>
    </div>
  );
}

function SyncSummary({ result }: { result: AnkiSyncResult }) {
  return (
    <div class="sync-summary">
      <dl class="kv kv--inline">
        <dt>{t('anki.resultCreated')}</dt>
        <dd>{result.created}</dd>
        <dt>{t('anki.resultUpdated')}</dt>
        <dd>{result.updated}</dd>
        <dt>{t('anki.resultUnchanged')}</dt>
        <dd>{result.unchanged}</dd>
        <dt>{t('anki.resultFailed')}</dt>
        <dd>{result.failed}</dd>
      </dl>

      {result.errors.length > 0 ? (
        <div class="sync-errors">
          <p>{t('anki.syncErrors')}</p>
          <ul>
            {result.errors.slice(0, 5).map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <p class="muted small">{t('anki.firstNotes')}</p>
      {result.notes.length === 0 ? (
        <p class="muted">{t('anki.noNotes')}</p>
      ) : (
        <ul class="note-keys">
          {result.notes.slice(0, 5).map((note) => (
            <li key={note.note_key}>
              <code>{note.note_key.slice(0, 16)}…</code>
              <span class="muted">
                {' '}
                {note.action} · {note.deck}/{note.model}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
